import {
  DocumentRecordsListSortBy,
  DocumentRecordsSortDirection,
  FatSeekingInterestPageInvestmentQuery,
  Security_Status,
  StrategyDetailsPageGetDocumentRecordQuery
} from '@/__generated__/graphql';
import { EditIcon } from '@/assets/icons/edit';
import Button from '@/components/basicComponents/button';
import { ITab, MainTabsMobile, TabsDetailsTitleMobile, TabsWrapMobile } from '@/components/basicComponents/tabs';
import { INVESTMENT_QUERY } from '@/components/dealCatalog-page/seekingInterest-page/queries';
import { IOfferingMaterialsAttachments } from '@/components/dealCatalog-page/types';
import Tag from '@/components/basicComponents/tag';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitleWrap } from '@/components/header/pageTitle';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { MANAGER_UPDATES_EDITABLE_ROLES } from '@/components/managerUpdates-page/constants';
import { UpdatesTab } from '@/components/strategyDetails-page/tabs/updatesTab';
import {
  STRATEGY_DETAILS_PAGE_GET_DOCUMENT_RECORD_QUERY,
  STRATEGY_DETAILS_PAGE_LIST_DOCUMENT_RECORDS_QUERY
} from '@/components/strategyDetails-page/tabs/updatesTab/queries';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { useCustomizationsData } from '@/hooks/useCustomizationsData';
import { useGoBack } from '@/hooks/useGoBack';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useQuery } from '@apollo/client';
import { TenantType } from '@trace-8/trace8-schema';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { SYSTEM_SETTINGS_QUERY } from '../settings-page/queries';
import { Details } from './details';
import BeginAllocation from './modals/beginAllocation';
import { DetailsTab } from './tabs/detailsTab';
import { InvestorsTab } from './tabs/investorsTab';
import {
  STRATEGY_DETAILS_PAGE_COMMITMENTS_BY_INVESTOR_TOTAL_QUERY,
  STRATEGY_DETAILS_PAGE_INVESTMENT_ENTITIES_BY_ADVISORY_FIRM_TOTAL_QUERY,
  STRATEGY_DETAILS_PAGE_INVESTMENT_ENTITIES_TOTAL_QUERY
} from './tabs/investorsTab/queries';
import { ResearchTab } from './tabs/researchTab';
import { StrategyDetailsTabsEnum } from './types';

const isTabExists = (tabs: ITab[], activeTab: StrategyDetailsTabsEnum): boolean => {
  if (!tabs.length || !activeTab) return false;
  return tabs.some((tab) => tab.value === activeTab);
};

export const StrategyDetails = () => {
  const navigate = useNavigate();
  const goBack = useGoBack();
  const params = useParams();
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const { data: customizationsData } = useCustomizationsData();

  const [searchParams, setSearchParams] = useSearchParams();

  const backToTitle = searchParams.get('backToTitle') || '';
  const activeTab = (searchParams.get('tab') as StrategyDetailsTabsEnum) || StrategyDetailsTabsEnum.DETAILS;

  const [investmentData, setInvestmentData] = useState<FatSeekingInterestPageInvestmentQuery['Investment'] | null>(null);
  const [strategyDetailsOfferingMaterialsAttachments, setStrategyDetailsOfferingMaterialsAttachments] = useState<IOfferingMaterialsAttachments[]>([]);
  const [tabs, setTabs] = useState<ITab[]>([]);
  const [isTabMenuOpen, setIsTabMenuOpen] = useState(false);
  const [documentRecordId, setDocumentRecordId] = useState<string | null>(null);
  const [descriptionSectionFields, setDescriptionSectionFields] = useState<StrategyDetailsPageGetDocumentRecordQuery['getDocumentRecord']['fields']>(
    []
  );
  const [listDocumentRecordsStatusFilter, setListDocumentRecordsStatusFilter] = useState<string[]>([]);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });
  const [bulkAllocateFeatureFlag, setBulkAllocateFeatureFlag] = useState<boolean>(false);
  const [showDescriptionTabZeroState, setShowDescriptionTabZeroState] = useState(false);

  useEffect(() => {
    if (MANAGER_UPDATES_EDITABLE_ROLES.some((role) => user.roles.includes(role)) && user.tenant.type === TenantType.PLATFORM_MANAGER) {
      setListDocumentRecordsStatusFilter(['Draft', 'Published']);
      return;
    }
    setListDocumentRecordsStatusFilter(['Published']);
  }, [user]);

  useQuery(SYSTEM_SETTINGS_QUERY, {
    skip: !user?.tenant?.id,
    variables: {
      data: {
        tenantId: user?.tenant?.id,
        type: 'featureFlag',
        key: 'bulkAllocate',
        allowNullTenantSettings: true
      }
    },
    onCompleted: (data) => {
      setBulkAllocateFeatureFlag(data?.systemSettings?.find((setting) => setting?.key === 'bulkAllocate')?.value === 'true' || false);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const { loading: loadingInvestment } = useQuery(INVESTMENT_QUERY, {
    variables: { id: params?.strategyId },
    skip: !params?.strategyId,
    onCompleted: (data) => {
      const filteredAttachments =
        data?.Investment?.offeringMaterialsAttachments?.filter((attachment) => !attachment?.asset?.assetKey?.includes('/mu/')) || [];

      setStrategyDetailsOfferingMaterialsAttachments(filteredAttachments);
      setInvestmentData(data.Investment);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const { loading: listDocumentStrategyDescriptionRecordsLoading } = useQuery(STRATEGY_DETAILS_PAGE_LIST_DOCUMENT_RECORDS_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !params?.strategyId,
    variables: {
      data: {
        investmentId: params?.strategyId,
        sortBy: DocumentRecordsListSortBy.Name,
        sortDirection: DocumentRecordsSortDirection.Asc,
        documentTypeFilter: ['sd'],
        limit: 1
      },
      fieldNames: []
    },
    onCompleted: (data) => {
      if (!data.listDocumentRecords.records.length) return;
      setDocumentRecordId(data.listDocumentRecords.records[0].id);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const { loading: documentRecordLoading } = useQuery(STRATEGY_DETAILS_PAGE_GET_DOCUMENT_RECORD_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !documentRecordId,
    variables: {
      id: documentRecordId
    },
    onCompleted: (data) => {
      setDescriptionSectionFields(data.getDocumentRecord.fields);
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const { data: listDocumentManagerUpdatesRecordsData, loading: listDocumentManagerUpdatesRecordsLoading } = useQuery(
    STRATEGY_DETAILS_PAGE_LIST_DOCUMENT_RECORDS_QUERY,
    {
      notifyOnNetworkStatusChange: true,
      skip: !params?.strategyId || !listDocumentRecordsStatusFilter.length,
      variables: {
        data: {
          investmentId: params?.strategyId,
          sortBy: DocumentRecordsListSortBy.Name,
          sortDirection: DocumentRecordsSortDirection.Asc,
          statusFilter: listDocumentRecordsStatusFilter,
          documentTypeFilter: ['mu'],
          limit: 1
        },
        fieldNames: ['Document Record Publish Date']
      },
      onError: (error) => {
        console.error(error);
      }
    }
  );

  const { data: advisoryFirmsTotalData } = useQuery(STRATEGY_DETAILS_PAGE_INVESTMENT_ENTITIES_BY_ADVISORY_FIRM_TOTAL_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !params?.strategyId,
    variables: {
      id: params?.strategyId
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const { data: investorsTotalData } = useQuery(STRATEGY_DETAILS_PAGE_COMMITMENTS_BY_INVESTOR_TOTAL_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !params?.strategyId,
    variables: {
      investmentId: params?.strategyId
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const { data: entitiesTotalData } = useQuery(STRATEGY_DETAILS_PAGE_INVESTMENT_ENTITIES_TOTAL_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !params?.strategyId,
    variables: {
      id: params?.strategyId
    },
    onError: (error) => {
      console.error(error);
    }
  });

  useEffect(() => {
    const newTabs: ITab[] = [];

    const advisoryFirmsTotal = advisoryFirmsTotalData?.InvestmentEntitiesByAdvisoryFirm?.total || 0;
    const investorsTotal = investorsTotalData?.CommitmentsByInvestor?.total || 0;
    const entitiesTotal = entitiesTotalData?.InvestmentEntities?.total || 0;

    const showInvestorsTab = advisoryFirmsTotal || investorsTotal || entitiesTotal;

    if (showInvestorsTab) {
      newTabs.push({ value: StrategyDetailsTabsEnum.INVESTORS, amount: null });
    }

    const managerUpdatesRecordsTotal = listDocumentManagerUpdatesRecordsData?.listDocumentRecords?.total || 0;

    const showUpdatesTab =
      user.tenant.type === TenantType.PLATFORM_MANAGER ||
      (managerUpdatesRecordsTotal && user.tenant.type === TenantType.ADVISORY_FIRM && showInvestorsTab);

    if (showUpdatesTab) {
      newTabs.push({ value: StrategyDetailsTabsEnum.UPDATES, amount: null });
    }

    if (customizationsData?.menuConfig?.dueDiligence) {
      newTabs.push({ value: StrategyDetailsTabsEnum.RESEARCH, amount: null });
    }

    setTabs(newTabs.length ? [{ value: StrategyDetailsTabsEnum.DETAILS, amount: null }, ...newTabs] : []);
  }, [advisoryFirmsTotalData, investorsTotalData, entitiesTotalData, listDocumentManagerUpdatesRecordsData]);

  useEffect(() => {
    if (!descriptionSectionFields) return;

    const hasFieldsValues = descriptionSectionFields?.slice(1)?.some((field) => field.value);
    setShowDescriptionTabZeroState(!hasFieldsValues && !strategyDetailsOfferingMaterialsAttachments?.length);
  }, [investmentData, descriptionSectionFields]);

  const onChangeActiveTab = (value: string) => {
    setSearchParams(
      (prev) => {
        prev.set('tab', value);
        return prev;
      },
      { replace: true }
    );
  };

  const openModifyInvestment = (id?: string) => {
    navigate(`/dealPipeline/modify?backToTitle=Strategy Details&investment=${encodeURIComponent(id)}`, {
      state: {
        investment: id
      }
    });
  };

  const openAllocatorPage = (id: string) => {
    if (!id) return;

    setSearchParams(
      (prev) => {
        prev.set('position', window.scrollY.toString());
        return prev;
      },
      { replace: true }
    );

    if (user && user.tenant.type === TenantType.PLATFORM_MANAGER) {
      navigate(`/dealPipeline/allocator/${id}?backToTitle=The Strategy`);
      return;
    }
    navigate(`/dealCatalog/allocator/${id}?backToTitle=The Strategy`);
  };

  const openEditDescriptionPage = (id: string) => {
    navigate(`/strategyDetails/editStrategyDescription/${id}?backToTitle=Strategy Details`);
  };

  const openForwardDealDocumentsPage = (id: string) => {
    if (customizationsData?.menuConfig?.transactions) {
      navigate(`/transactions/forwardDealDocuments/${id}?backToTitle=Strategy Details`);
      return;
    }
    if (customizationsData?.menuConfig?.pendingCommitments) {
      navigate(`/pendingCommitments/forwardDealDocuments/${id}?backToTitle=Strategy Details`);
      return;
    }
  };

  const openModalWindow = (type: string) => {
    setModalWindow({ type, isOpen: true });
  };

  const closeModalWindow = () => {
    setModalWindow({ ...modalWindow, isOpen: false });
  };

  const changeTab = (value: string) => {
    onChangeActiveTab(value);
    setIsTabMenuOpen(false);
  };

  if (loadingInvestment || listDocumentStrategyDescriptionRecordsLoading || listDocumentManagerUpdatesRecordsLoading || documentRecordLoading) {
    return <LoaderOnWholeScreen />;
  }

  return (
    <>
      {modalWindow.isOpen && modalWindow.type === 'begin-allocation' && (
        <BeginAllocation
          isOpen={modalWindow.isOpen}
          minimum={investmentData?.minimum || 0}
          investmentType={investmentData?.type || ''}
          investmentId={investmentData?.id || ''}
          onClose={closeModalWindow}
        />
      )}
      <MainWrap>
        <Header
          modalControl={
            <GoBackButton
              handleClose={() => goBack({ fallBack: user.tenant.type === TenantType.PLATFORM_MANAGER ? '/dealPipeline' : '/dealCatalog' })}
              backToTitle={backToTitle}
            />
          }
        />
        <ManagerNameWrap withBorder>{investmentData?.managerId ? `Manager: ${investmentData.managerId}` : 'No Manager Set'}</ManagerNameWrap>
        <PageTitleWrap withBorder={false}>
          {investmentData?.name ?? ''}
          {user && user.tenant.type === TenantType.PLATFORM_MANAGER && (
            <IconWrap onClick={() => openModifyInvestment(investmentData?.id)}>
              <EditIcon fill={themeVar('action.primary')} />
            </IconWrap>
          )}
          <ButtonsWrapper>
            {investmentData?.workflowInstance?.currentState?.label && (
              <Tag title={investmentData?.workflowInstance?.currentState?.label} size="lg" styleType="neutral" />
            )}
            {investmentData?.securityStatus === Security_Status.Open && (
              <>
                {user && user.tenant.type === TenantType.ADVISORY_FIRM && bulkAllocateFeatureFlag && (
                  <Button styleType="outline" size="md" onClick={() => openAllocatorPage(investmentData?.id)}>
                    Bulk Allocate
                  </Button>
                )}
                <Button size="md" onClick={() => openModalWindow('begin-allocation')}>
                  Begin Allocation
                </Button>
              </>
            )}
          </ButtonsWrapper>
        </PageTitleWrap>
        <Details
          investmentId={params?.strategyId}
          summary={descriptionSectionFields[0]?.value ?? ''}
          tabs={tabs}
          activeTab={activeTab}
          isTabMenuOpen={isTabMenuOpen}
          showMinimalData={showDescriptionTabZeroState && !tabs.length}
          showGreyTabs={activeTab !== StrategyDetailsTabsEnum.DETAILS && activeTab !== StrategyDetailsTabsEnum.RESEARCH}
          showInfoTags={activeTab === StrategyDetailsTabsEnum.DETAILS}
          tabsPlacement="top"
          constrainWidth={activeTab === StrategyDetailsTabsEnum.DETAILS}
          setIsTabMenuOpen={setIsTabMenuOpen}
          setActiveTab={onChangeActiveTab}
        />
      </MainWrap>
      <MainWrap>
        {isTabMenuOpen && isMobile && (
          <PaddingWrap>
            <MainTabsMobile>
              {tabs.map((tab, tabId) => (
                <TabsWrapMobile key={tab.value + tabId} onClick={() => changeTab(tab.value)} isActive={activeTab === tab.value}>
                  <TabsDetailsTitleMobile isActive={activeTab === tab.value}>{tab.value}</TabsDetailsTitleMobile>
                </TabsWrapMobile>
              ))}
            </MainTabsMobile>
          </PaddingWrap>
        )}
        {!isTabMenuOpen && (
          <>
            {activeTab === StrategyDetailsTabsEnum.DETAILS && (
              <StrategyDetailsPaddingWrap>
                <DetailsTab
                  offeringMaterialsAttachments={strategyDetailsOfferingMaterialsAttachments ?? []}
                  documentRecordId={documentRecordId}
                  descriptionSectionFields={descriptionSectionFields}
                  showZeroState={showDescriptionTabZeroState}
                  openEditDescriptionPage={() => openEditDescriptionPage(investmentData?.id)}
                  openForwardDealDocumentsPage={() => openForwardDealDocumentsPage(investmentData?.id)}
                />
              </StrategyDetailsPaddingWrap>
            )}
            {activeTab === StrategyDetailsTabsEnum.INVESTORS && isTabExists(tabs, activeTab) && (
              <StrategyDetailsPaddingWrap>
                <InvestorsTab />
              </StrategyDetailsPaddingWrap>
            )}
            {activeTab === StrategyDetailsTabsEnum.UPDATES && isTabExists(tabs, activeTab) && (
              <UpdatesTab
                managerId={investmentData?.managerId || null}
                offeringMaterialsAttachments={investmentData?.offeringMaterialsAttachments ?? []}
                backToTitle="Strategy Details"
              />
            )}
            {activeTab === StrategyDetailsTabsEnum.RESEARCH && isTabExists(tabs, activeTab) && <ResearchTab investmentData={investmentData} />}
          </>
        )}
      </MainWrap>
    </>
  );
};

const ManagerNameWrap = styled(PageTitleWrap)`
  font-size: 18px;
  font-weight: 600;
  line-height: 18px;
  color: ${themeVar('font.strong')};
`;

const IconWrap = styled.div`
  display: flex;
  cursor: pointer;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 20px;
  margin-left: auto;
  padding: 8px 0;
  max-width: 400px;
  width: 100%;
  min-width: max-content;
`;

const StrategyDetailsPaddingWrap = styled(PaddingWrap)`
  padding-top: 40px;
  padding-bottom: 40px;
`;
